import { DESCRIPTION_COPY_LIMITS, TITLE_COPY_LIMITS } from "../copy-policy";
/**
 * Chapters — one editorial passage of three narrative chapters with a collage
 * of pictures.
 *
 * Each chapter has its own copy, an optional call to action, and three
 * pictures. The arrangement of those pictures — position, overlap, which sits
 * in front, how they move as a visitor scrolls — is presentation-owned by
 * @diality/ui, so authors supply nine pictures and no coordinates.
 *
 * Everything is declared at BLOCK level rather than as a repeated group, and
 * deliberately: a picture held by a repeated item has no write path at any
 * layer (the repeat editor renders such slots disabled, and no route, service or
 * repository can save one), so a chapter list carrying its own pictures could
 * never actually be filled in. Three chapters is also the passage's shape rather
 * than a length an Editor chooses — the collage is composed for three.
 *
 * The nine slots and their two sizes come from {@link CHAPTERS_PICTURE_SLOTS},
 * which the validator shares, so the sizes the picker enforces and the sizes a
 * stored reference is named by cannot drift apart.
 */
import { CHAPTERS_MAX_LENGTHS, CHAPTERS_PICTURE_SLOTS, CHAPTER_COUNT, chapterPictureSlotKeys, } from "../chapters";
/** `sectionOne` … `sectionThree`, the prefix each chapter's scalars carry. */
const SECTION_PREFIXES = ["sectionOne", "sectionTwo", "sectionThree"];
function chapterScalarSlots() {
    return SECTION_PREFIXES.flatMap((prefix, index) => {
        const chapter = index + 1;
        return [
            {
                key: `${prefix}Eyebrow`,
                label: `Chapter ${chapter} · Eyebrow`,
                type: "text",
                maxLength: CHAPTERS_MAX_LENGTHS.eyebrow,
            },
            {
                key: `${prefix}Title`,
                label: `Chapter ${chapter} · Title`,
                type: "text",
                ...TITLE_COPY_LIMITS,
            },
            {
                key: `${prefix}CtaLabel`,
                label: `Chapter ${chapter} · Call to action label`,
                type: "text",
            },
            {
                key: `${prefix}CtaHref`,
                label: `Chapter ${chapter} · Call to action link`,
                type: "url",
            },
        ];
    });
}
/** The scalar key holding chapter `position`'s named field. 1-based. */
export function chapterScalarKey(position, field) {
    const prefix = SECTION_PREFIXES[position - 1];
    if (!prefix)
        throw new Error(`No chapter at position ${position}`);
    return `${prefix}${field}`;
}
/** The document slot holding chapter `position`'s description. 1-based. */
export function chapterDescriptionKey(position) {
    const prefix = SECTION_PREFIXES[position - 1];
    if (!prefix)
        throw new Error(`No chapter at position ${position}`);
    return `${prefix}Description`;
}
export const chaptersSchema = {
    componentId: "Chapters",
    scalarSlots: chapterScalarSlots(),
    documentSlots: Array.from({ length: CHAPTER_COUNT }, (_, index) => ({
        key: chapterDescriptionKey(index + 1),
        label: `Chapter ${index + 1} · Description`,
        ...DESCRIPTION_COPY_LIMITS,
    })),
    mediaSlots: CHAPTERS_PICTURE_SLOTS.map((slot) => ({
        key: slot.key,
        label: slot.label,
        // Single-target each: one picture PER SLOT, chosen, framed and refused
        // independently. A multi-target slot would be one picture framed nine ways,
        // which is not what a collage of nine different pictures is.
        targets: [{ width: slot.width, height: slot.height, aspectRatio: "4:3" }],
    })),
    /**
     * One section per chapter, because a chapter is the unit an author works in.
     *
     * The three arrays above are a storage shape. Rendered in array order they
     * put chapter 1's title at the top of the panel, its description eleven
     * fields down and its feature picture twenty controls below that — so
     * composing one chapter means scrolling past the other two, three times. The
     * `Chapter N · ` prefix the labels carry was the only thing saying which
     * field belonged to which chapter, and nothing read it.
     *
     * Within a section the order is the order a VISITOR reads: eyebrow, title,
     * description, the call to action — then the collage that sits beside all of
     * it. Keys come from the shared helpers, so no slot key is spelled twice.
     */
    sections: Array.from({ length: CHAPTER_COUNT }, (_, index) => {
        const position = index + 1;
        return {
            key: `chapter${position}`,
            label: `Chapter ${position}`,
            slotKeys: [
                chapterScalarKey(position, "Eyebrow"),
                chapterScalarKey(position, "Title"),
                chapterDescriptionKey(position),
                chapterScalarKey(position, "CtaLabel"),
                chapterScalarKey(position, "CtaHref"),
                ...chapterPictureSlotKeys(position),
            ],
        };
    }),
};
//# sourceMappingURL=chapters.js.map